import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash, FolderOpen, PaperPlaneTilt, Copy, CheckCircle, PencilSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { SearchableSelect } from "../components/ui/searchable-select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import UserMultiSelect from "../components/UserMultiSelect";
import TicketCountRow from "../components/TicketCountRow";
import { ROLE_LABELS } from "../lib/utils";

/* ---------- Invite-a-client dialog (per project) ---------- */
function InviteDialog({ project }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("client_user");
  const [scope, setScope] = useState("project"); // project | all
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const reset = () => { setEmail(""); setRole("client_user"); setScope("project"); setError(""); setResult(null); setCopied(false); };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/invites", {
        email: email.trim(),
        role,
        organization_id: project.organization_id,
        project_id: scope === "project" ? project.id : null,
        all_projects: scope === "all",
      });
      setResult(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(result.invite_url);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <button className="text-slate-400 hover:text-blue-600" title="Invite a client to this project">
          <PaperPlaneTilt size={16} />
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Invite to {project.name}</DialogTitle></DialogHeader>

        {result ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
              <CheckCircle size={16} weight="fill" />
              {result.email_sent ? "Invitation email sent." : "Invitation created — email not configured, share the link below."}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Invite link</Label>
              <div className="flex gap-2">
                <Input readOnly value={result.invite_url} className="text-xs font-mono" />
                <Button type="button" variant="outline" onClick={copy} className="shrink-0">
                  {copied ? <CheckCircle size={14} className="text-emerald-600" /> : <Copy size={14} />}
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={reset}>Invite another</Button>
              <Button type="button" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setOpen(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Email</Label>
              <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@client.com" data-testid="invite-email-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Role</Label>
                <SearchableSelect
                  value={role}
                  onValueChange={setRole}
                  options={[
                    { value: "client_user", label: ROLE_LABELS.client_user },
                    { value: "client_admin", label: ROLE_LABELS.client_admin },
                  ]}
                  testId="invite-role-select"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Access</Label>
                <SearchableSelect
                  value={scope}
                  onValueChange={setScope}
                  options={[
                    { value: "project", label: "This project only" },
                    { value: "all", label: `All projects of ${project.organization_name || "this client"}` },
                  ]}
                />
              </div>
            </div>
            <p className="text-xs text-slate-400">They'll get an email link to set a password and join automatically.</p>
            {error && <div className="text-xs text-rose-600">{error}</div>}
            <DialogFooter>
              <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="invite-submit-button">
                {busy ? "Sending…" : "Send invitation"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Page ---------- */
export default function Projects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [assignable, setAssignable] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", organization_id: "", project_type: "web", assign_user_ids: [] });
  const [error, setError] = useState("");
  const canManage = user.role === "super_admin" || user.role === "project_manager";
  const canInvite = ["super_admin", "project_manager", "client_admin"].includes(user.role);

  const refresh = () => api.get("/projects").then((r) => setProjects(r.data));
  useEffect(() => {
    refresh();
    api.get("/organizations").then((r) => setOrgs(r.data));
    api.get("/masters/project_type?active_only=true").then((r) => setProjectTypes(r.data)).catch(() => {});
    if (canManage) api.get("/users").then((r) => setAssignable(r.data.filter((u) => !["super_admin", "project_manager"].includes(u.role))));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const emptyForm = { name: "", description: "", organization_id: "", project_type: "web", assign_user_ids: [] };

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm, project_type: projectTypes[0]?.value || "web" }); setError(""); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name || "", description: p.description || "", organization_id: p.organization_id || "", project_type: p.project_type || "web", assign_user_ids: [] });
    setError("");
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editing) {
        await api.patch(`/projects/${editing.id}`, {
          name: form.name, description: form.description,
          organization_id: form.organization_id, project_type: form.project_type,
        });
      } else {
        await api.post("/projects", form);
      }
      setOpen(false);
      setForm(emptyForm);
      setEditing(null);
      refresh();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete project?")) return;
    try {
      await api.delete(`/projects/${id}`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <div className="space-y-5" data-testid="projects-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold">Workspace</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">Projects</h1>
          <p className="text-sm text-slate-500 mt-1">Each ticket lives inside a project — website, software or mobile app.</p>
        </div>
        {canManage && (
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-project-button">
            <Plus size={14} weight="bold" className="mr-1.5" /> New project
          </Button>
        )}
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit project" : "Create project"}</DialogTitle></DialogHeader>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="project-form">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Project name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Client / Organization</Label>
                  <SearchableSelect
                    value={form.organization_id}
                    onValueChange={(v) => setForm({ ...form, organization_id: v })}
                    options={orgs.map((o) => ({ value: o.id, label: o.name }))}
                    placeholder="Select client"
                    testId="project-org-select"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Type</Label>
                  <SearchableSelect
                    value={form.project_type}
                    onValueChange={(v) => setForm({ ...form, project_type: v })}
                    options={projectTypes.map((pt) => ({ value: pt.value, label: pt.label }))}
                    placeholder="Select type"
                    testId="project-type-select"
                  />
                </div>
                {!editing && (
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Assign users (optional)</Label>
                    <UserMultiSelect users={assignable} value={form.assign_user_ids}
                      onChange={(ids) => setForm({ ...form, assign_user_ids: ids })}
                      placeholder="Grant project access to users…" />
                    <p className="text-[11px] text-slate-400">Selected users get access to this project immediately.</p>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Description</Label>
                  <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="project-desc-input" />
                </div>
                {error && <div className="text-xs text-rose-600">{error}</div>}
                <DialogFooter>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="project-submit-button">{editing ? "Save changes" : "Create"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {projects.length === 0 && (
          <div className="col-span-full text-center text-slate-400 py-16 border border-dashed border-slate-200 rounded-md">No projects yet.</div>
        )}
        {projects.map((p) => (
          <div key={p.id} className="surface-card p-5 hover:border-slate-300 transition-colors" data-testid={`project-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center">
                <FolderOpen size={20} weight="regular" />
              </div>
              <div className="flex items-center gap-2.5">
                {canInvite && <InviteDialog project={p} />}
                {canManage && (
                  <button onClick={() => openEdit(p)} className="text-slate-400 hover:text-blue-600" title="Edit project" data-testid={`project-edit-${p.id}`}>
                    <PencilSimple size={16} />
                  </button>
                )}
                {user.role === "super_admin" && (
                  <button onClick={() => remove(p.id)} className="text-slate-400 hover:text-rose-600" title="Delete project" data-testid={`project-delete-${p.id}`}>
                    <Trash size={16} />
                  </button>
                )}
              </div>
            </div>
            <Link to={`/projects/${p.id}`} className="font-display font-semibold text-slate-900 mt-3 hover:underline block">{p.name}</Link>
            <p className="text-xs text-slate-500 mt-1 line-clamp-2 min-h-[2rem]">{p.description || "No description provided."}</p>
            <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500 flex justify-between">
              <span>{p.organization_name || "—"}</span>
              <span className="capitalize">{projectTypes.find((pt) => pt.value === p.project_type)?.label || p.project_type}</span>
            </div>
            <TicketCountRow projectId={p.id} counts={p.ticket_counts} className="mt-3" />
          </div>
        ))}
      </div>
    </div>
  );
}
